import { t } from '@glory/i18n';
import { LogOut, Shield, User } from 'lucide-react';
import type { ReactNode } from 'react';

import { Avatar } from '../components/avatar.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/dropdown-menu.tsx';
import { type ThemePreference, useTheme } from './theme.tsx';

/** Личный пункт меню: «Уведомления», «Почта» — всё, что человек настраивает для себя. */
export interface UserMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly onSelect: () => void;
}

/** Меню пользователя: всё личное в одном месте — профиль, уведомления, почта, безопасность, тема, выход. */
export function UserMenu({
  name,
  email,
  avatarUrl,
  onProfile,
  personal = [],
  onSecurity,
  onLogout,
}: {
  name: string;
  email: string;
  avatarUrl?: string | null;
  /** Страница профиля; без неё пункт не показывается. */
  onProfile?: (() => void) | undefined;
  /** Личные настройки перед «Безопасностью». */
  personal?: readonly UserMenuItem[];
  onSecurity: () => void;
  onLogout: () => void;
}) {
  const { preference, setPreference } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full" aria-label={t('shell.profile')}>
        <Avatar name={name} src={avatarUrl ?? null} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <div className="px-2 py-1.5">
          <div className="truncate font-bold">{name}</div>
          <div className="truncate text-small text-muted">{email}</div>
        </div>
        <DropdownMenuSeparator />
        {onProfile ? (
          <DropdownMenuItem icon={<User />} onSelect={onProfile}>
            {t('shell.profile')}
          </DropdownMenuItem>
        ) : null}
        {personal.map((entry) => (
          <DropdownMenuItem key={entry.id} icon={entry.icon} onSelect={entry.onSelect}>
            {entry.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem icon={<Shield />} onSelect={onSecurity}>
          {t('shell.security')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('shell.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => {
            setPreference(value as ThemePreference);
          }}
        >
          <DropdownMenuRadioItem value="system">{t('shell.themeSystem')}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">{t('shell.themeLight')}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">{t('shell.themeDark')}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<LogOut />} onSelect={onLogout}>
          {t('shell.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
